import { useQuery } from '@apollo/client';
import { MoreVert } from '@mui/icons-material';
import { Divider, Menu as MuiMenu } from '@mui/material';
import { useMemo, useState, type ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeleteContent, useMoveContent } from '~/api/content';
import { useDeleteContentFolder, useUpdateContentFolder } from '~/api/content-folders';
import { useCopyContentItem, useUpgradeContentItem } from '~/api/content-items';
import { useCreateOverlay, useDeleteOverlay } from '~/api/overlays';
import { IconButton } from '~/components/IconButton';
import { ActionButton } from '~/components/button';
import { MoveFolderDialog } from '~/components/dialogs/MoveFolderDialog';
import { MoveToFolderDialog } from '~/components/dialogs/MoveToFolderDialog';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreDeleteAction,
  mergeGridActionProps,
  type GridActionCellProps,
} from '~/components/table';
import { useAppContext } from '~/contexts';
import { AuthorizableType } from '~/generated/graphql';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLocation } from '~/hooks/location';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import { ContentMoveDocument } from '../../components';
import type {
  ContentFolderView__ContentFolder,
  ContentFolderView__ContentItem,
  ContentFolderView__MediaItem,
} from '../ContentFolderView.generated';
import { AuthorizableAuthDocument } from '../ContentFolderView.generated';
import { useFilterParams } from '../lib';
import {
  ContentMenuContentFolder,
  ContentMenuContentItem,
  ContentMenuMediaItem,
  RenameFolderDialog,
} from './';

export interface ContentMenuProps extends GridActionCellProps {
  buttonType?: 'action' | 'icon';
  item:
    | ContentFolderView__ContentFolder
    | ContentFolderView__ContentItem
    | ContentFolderView__MediaItem;
  Icon?: ElementType;
}

export const ContentMenu = ({
  buttonType = 'icon',
  item,
  Icon = MoreVert,
  ...cellProps
}: ContentMenuProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<typeof item>();
  const mediaActionProps = mergeGridActionProps(moreTableActionProps(item), cellProps);
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [confirmUpgrade, confirmUpgradeProps] = useConfirmDialog();
  const [deleteContent] = useDeleteContent();
  const [deleteFolder] = useDeleteContentFolder();
  const [copyApp] = useCopyContentItem();
  const [createOverlay] = useCreateOverlay();
  const [deleteOverlay] = useDeleteOverlay();
  const [upgradeApp] = useUpgradeContentItem();
  const [moveContent, moveContentInfo] = useMoveContent();
  const [updateContentFolder, updateContentFolderInfo] = useUpdateContentFolder();
  const [moveContentDialogOpen, setMoveContentDialogOpen] = useState(false);
  const [moveFolderDialogOpen, setMoveFolderDialogOpen] = useState(false);
  const [renameFolderDialogOpen, setRenameFolderDialogOpen] = useState(false);
  const { kind, tags } = useFilterParams();

  const menuOpen = Boolean(moreMenuProps.anchorEl);
  const defaultAuth = { value: false, reasons: null } as const;

  const { data: authData } = useQuery(AuthorizableAuthDocument, {
    variables: {
      networkId: currentNetwork.id,
      items: [{ id: item.id, type: AuthorizableType[item.__typename] }],
    },
    skip: !menuOpen,
  });

  const auth = authData?.network?.authorizable[0];
  const canDestroy = auth?.canDestroy ?? defaultAuth;
  const canUpdate = auth?.canUpdate ?? defaultAuth;
  const canUpgrade = auth?.__typename === 'ContentItem' ? auth.canUpgrade : defaultAuth;

  const { data: contentMoveData } = useQuery(ContentMoveDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !moveContentDialogOpen && !moveFolderDialogOpen,
  });
  const editableFolderIds = useMemo(
    () =>
      contentMoveData?.network?.contentFolders.filter((x) => x.canUpdate.value).map((x) => x.id) ??
      [],
    [contentMoveData],
  );

  const deletePrompt =
    item.__typename === 'ContentFolder'
      ? t('pages.content.dialogs.delete_folder.prompt_folder')
      : item.__typename === 'MediaItem'
      ? t('pages.content.dialogs.delete_folder.prompt_media')
      : t('pages.content.dialogs.delete_folder.prompt_content');

  const deleteTitle =
    item.__typename === 'ContentFolder'
      ? t('pages.content.dialogs.delete_folder.title_folder')
      : t('pages.content.dialogs.delete_folder.title_content');

  return (
    <>
      {buttonType === 'icon' ? (
        <IconButton
          label={t('pages.content.actions.actions_tooltip')}
          color="secondary"
          onClick={mediaActionProps.onClick}
          size="small"
        >
          <Icon />
        </IconButton>
      ) : (
        <ActionButton {...mediaActionProps} />
      )}
      <MuiMenu {...moreMenuProps}>
        {item.__typename === 'ContentFolder' && (
          <ContentMenuContentFolder
            canUpdate={canUpdate.value}
            item={item}
            onMove={() => {
              assert(moreMenuProps.context != null, 'media: no context');
              setMoveFolderDialogOpen(true);
              moreMenuProps.onClose();
            }}
            onRename={() => {
              setRenameFolderDialogOpen(true);
              moreMenuProps.onClose();
            }}
          />
        )}
        {item.__typename === 'ContentItem' && (
          <ContentMenuContentItem
            canUpdate={canUpdate.value}
            canUpgrade={canUpgrade.value}
            item={item}
            onCopy={async () => {
              assert(moreMenuProps.context != null, 'media: no context');
              const { data } = await copyApp({ variables: { contentItemId: item.id } });
              const id = data?.copyContentItem?.contentItem?.id;
              if (id) {
                navigate(`apps/${id}/settings${location.search}`, {
                  state: { returnTo: location.fullPath },
                });
              }
              moreMenuProps.onClose();
            }}
            onOverlay={async () => {
              assert(moreMenuProps.context != null, 'media: no context');
              await createOverlay({ variables: { item: { contentItemId: item.id } } });
              moreMenuProps.onClose();
            }}
            onRemoveOverlay={async () => {
              assert(moreMenuProps.context != null, 'media: no context');
              assert(item.overlay != null, 'content item is not an overlay');
              await deleteOverlay({ variables: { input: { overlayId: item.overlay.id } } });
              moreMenuProps.onClose();
            }}
            onMove={() => {
              assert(moreMenuProps.context != null, 'media: no context');
              setMoveContentDialogOpen(true);
              moreMenuProps.onClose();
            }}
            onUpgrade={async () => {
              assert(moreMenuProps.context != null, 'media: no context');
              if (!(await confirmUpgrade())) {
                moreMenuProps.onClose();
                return;
              }
              await upgradeApp({ variables: { contentItemId: item.id } });
              moreMenuProps.onClose();
            }}
          />
        )}
        {item.__typename === 'MediaItem' && (
          <ContentMenuMediaItem
            canUpdate={canUpdate.value}
            item={item}
            onMove={() => {
              assert(moreMenuProps.context != null, 'media: no context');
              setMoveContentDialogOpen(true);
              moreMenuProps.onClose();
            }}
            onOverlay={async () => {
              assert(moreMenuProps.context != null, 'media: no context');
              await createOverlay({ variables: { item: { mediaItemId: item.id } } });
              moreMenuProps.onClose();
            }}
            onRemoveOverlay={async () => {
              assert(moreMenuProps.context != null, 'media: no context');
              assert(item.overlay != null, 'media item is not an overlay');
              await deleteOverlay({ variables: { input: { overlayId: item.overlay.id } } });
              moreMenuProps.onClose();
            }}
          />
        )}
        <Divider />
        <MoreDeleteAction
          {...moreActionProps}
          disabled={!canDestroy.value}
          onClick={async () => {
            assert(moreMenuProps.context !== undefined);
            if (!(await confirmDelete())) return;
            if (item.__typename === 'ContentFolder') {
              await deleteFolder({
                variables: {
                  condition: { kind, tags },
                  input: { contentFolderId: moreMenuProps.context.id },
                },
              });
            } else {
              await deleteContent({
                variables: {
                  items: [
                    {
                      contentableId: moreMenuProps.context.id,
                      contentableType:
                        moreMenuProps.context.__typename === 'ContentItem' ? 'APP' : 'MEDIA',
                    },
                  ],
                },
              });
            }
            moreMenuProps.onClose();
          }}
          tooltip={canDestroy.reasons?.fullMessages.join(',')}
        />
      </MuiMenu>
      <MoveToFolderDialog
        editableFolderIds={editableFolderIds}
        onSubmit={async (targetFolderId) => {
          await moveContent({
            refetchQueries: ['ContentFolderView', 'ContentSearch'],
            variables: {
              contentFolderId: targetFolderId,
              items: [
                {
                  contentableId: item.id,
                  contentableType: item.__typename === 'ContentItem' ? 'APP' : 'MEDIA',
                },
              ],
            },
          });
        }}
        loading={moveContentInfo.loading}
        onClose={() => setMoveContentDialogOpen(false)}
        open={moveContentDialogOpen}
        i18nKey="pages.content.dialogs.move_content.title"
      />
      <MoveFolderDialog
        editableFolderIds={editableFolderIds}
        folderId={item.id}
        onSubmit={async (targetFolderId) => {
          await updateContentFolder({
            refetchQueries: ['ContentFolderView'],
            variables: { contentFolderId: item.id, patch: { parentId: targetFolderId } },
          });
        }}
        loading={updateContentFolderInfo.loading}
        onClose={() => setMoveFolderDialogOpen(false)}
        open={moveFolderDialogOpen}
        i18nKey="common.dialogs.move_folder.title"
      />
      {item.__typename === 'ContentFolder' && (
        <RenameFolderDialog
          contentFolder={item}
          onClose={() => setRenameFolderDialogOpen(false)}
          open={renameFolderDialogOpen}
        />
      )}
      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('common.dialogs.delete')}
        deleteConfirm
        prompt={deletePrompt}
        title={deleteTitle}
      />
      <ConfirmDialog
        {...confirmUpgradeProps}
        confirm={t('common.dialogs.confirm')}
        prompt={t('pages.content.dialogs.upgrade_app.prompt')}
        title={t('pages.content.dialogs.upgrade_app.title')}
      />
    </>
  );
};
